import {useAppContext, useUserContext, useBehavioralContext} from "../../../../contexts";
import {api} from "../../../../services";
import useGtmContext from "../../../../contexts/GTMContext";
import {useState, useCallback, useEffect} from "preact/hooks";
import {dataFormatter} from "./useCartResume.helper";

// To be used in development mode.
// Not necessary for now, but we are leaving here just in case we need it sometime.
const {MOCK_DATA} = require("./useCartResume.mock.js");

export const useCartResume = (props) => {
    // Destructured props
    const {handleCartLoader} = props;

    // State to store all cart data
    const [userCartData, setUserCartData] = useState({});
    // Flags whether the content is ready to be displayed
    const [isReady, setIsReady] = useState(false);

    // Retrieves the method to trigger the full page loader component
    const {
        setFullPageLoader
    } = useAppContext();
    // Retrieves the session form key
    const [{sessionFormKey}, {}, {cartData, setCartData}] = useUserContext();
    // Retrieves all the cart related stuff
    const {sidebarCartData} = useBehavioralContext();
    // GTM feature data
    const {handleAddProductEvent} = useGtmContext();

    // Method to fetch data from the backend
    const fetchData = useCallback(async () => {
        // If the cart is closed, then there's no need to perform the operation
        if (!sidebarCartData.isOpen) {
            return;
        }

        // Activates the cart loader component
        handleCartLoader(true);

        try {
            // Tries to perform the request to the backend
            let response = await api.get("/v1/cart");

            // Sets the response data to the userCartData state
            // setUserCartData(dataFormatter(MOCK_DATA));
            setUserCartData(dataFormatter(response.data));
            // Flags the content as ready to be displayed
            setIsReady(true);
        } catch (err) {
            console.error("Error while fetching data:", err);
        } finally {
            // Deactivates the cart loader component
            handleCartLoader(false);
        }
    }, [sidebarCartData.isOpen]);

    const getProductGTMData = (product) => {
        const categoriesNameList = product.productCategories.map((item) => (item.name));
        const categoriesString = categoriesNameList.join(" | ");

        return {
            id: product.id || null,
            name: product.name || null,
            category: categoriesString || null,
            variant: product.unity || null,
            brand: 'Sabin',
            quantity: 1,
            price: product.price || null,
        }
    };

    // Method to update the item in the cart
    const updateCart = async props => {
        // Destructured props
        const {
            item,
            action
        } = props;

        // Prevents the method from running if the required data isn't ready
        if (!item.id || !action) {
            return;
        }

        try {
            handleCartLoader(true);

            let response;

            // Tries to perform the request to the backend
            if (action.toLowerCase() !== "delete") {
                response =
                    await api.get(
                        `/v1/cart/update?itemId=${item.id}&action=${action.toLowerCase()}&form_key=${userCartData?.formKey}`
                    );

                handleAddProductEvent(getProductGTMData(item));
            } else {
                response =
                    await api.get(
                        `/v1/cart/remove?stockItem=${item.stockItem}&form_key=${userCartData?.formKey}`
                    );
            }

            // Sets the response data to the userCartData state
            setUserCartData(dataFormatter(response.data));
            // Updates the context cart items amount
            setCartData(prevState => {
                return {
                    ...prevState,
                    qtyItems: response.data.qtyItems
                }
            })

            // Sets the loader state to false
            handleCartLoader(false);
        } catch (err) {
            console.error("Error while updating cart:", err);
        }
    }

    // Method to handle the budget cancel action
    const handleBudgetCancelAction = async () => {
        try {
            // Activates the full page loader component
            setFullPageLoader(true);

            // Actually performs the request to the backend
            let response = await api.post("/nad/nad/cancel", {
                form_key: sessionFormKey
            });

            // Checks for any errors
            if (response.data.error) {
                console.log("Error while canceling budget.");

                // Deactivates the full page loader component
                setFullPageLoader(false);

                return;
            }

            // Redirects to the home page if operation is successful
            window.location.replace("/");
        } catch (err) {
            console.log("Error while canceling budget: ", err);

            // Deactivates the full page loader component
            setFullPageLoader(false);
        }
    }

    useEffect(() => {
        fetchData().then();
    }, [fetchData]);

    return {
        data: userCartData,
        isReady,
        updateCart,
        handleBudgetCancelAction
    }
}
